import { T, useGT } from 'gt-react';
import { ArrowCounterClockwiseIcon, SparkleIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { CanvasGenerationControls } from './useCanvasGeneration';

/**
 * The "prompt the canvas" bar: a description input plus Generate / Revert,
 * driven entirely by a {@link CanvasGenerationControls} bundle from
 * `useCanvasGeneration`. Presentational and surface-agnostic — mount it above
 * any editable canvas.
 */
export function CanvasPromptBar({
  controls,
  placeholder,
  disabled = false,
}: {
  controls: CanvasGenerationControls;
  /** Surface-specific example copy; falls back to a neutral prompt. */
  placeholder?: string;
  /** Disable the whole bar (e.g. no dataset to chart yet). */
  disabled?: boolean;
}) {
  const gt = useGT();
  const { prompt, setPrompt, isGenerating, canRevert, generate, revert } =
    controls;
  const busy = isGenerating || disabled;

  return (
    <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
      <Input
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void generate();
        }}
        aria-label={gt('Describe the layout to generate')}
        placeholder={
          placeholder ??
          gt('Describe what you want to see and let AI build the layout')
        }
        className="h-8 flex-1 text-xs"
        disabled={busy}
      />
      {canRevert && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs"
          onClick={revert}
          disabled={busy}
          title={gt('Restore the layout from before the last generation')}
        >
          <ArrowCounterClockwiseIcon className="h-3.5 w-3.5" />
          <T>Revert</T>
        </Button>
      )}
      <Button
        size="sm"
        className="h-8 gap-1 text-xs"
        onClick={() => void generate()}
        disabled={busy || !prompt.trim()}
      >
        <SparkleIcon className="h-3.5 w-3.5" />
        {isGenerating ? gt('Generating…') : gt('Generate')}
      </Button>
    </div>
  );
}
